import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';

// Components
import { Text } from '@doordash/design-language';
import ModifierView from 'components/cart-item-editor/ModifierView';
import { Form, Radio } from 'top-component-library';

// Hooks
import { useItemSheet } from 'hooks/use-item-sheet';
import { useStores } from 'hooks/use-stores';

// Tracking
import { itemModalTrackingEvents } from 'integrations/segment/tracking-events';
import { cartFormatting } from 'integrations/segment/util/formatting';

// Types
import { type MenuItemModifierGroup } from 'api/types';
import { ModifierGroupHeading, OptionalGroup, RequiredGroup, WarningIcon } from '../styles';
import { Body, Container, CustomCheckbox, Description, Header, Heading } from './styles';

function ModifierGroupView({ modGroup }: { modGroup: MenuItemModifierGroup }) {
  const {
    cartItem,
    displayedItem,
    submitPressed,
    addHTMLElementError,
    removeHTMLElementError,
    removeModWithMissingSelections,
  } = useItemSheet();
  const groupRef = useRef<HTMLDivElement>(null);
  const { menuDataStore, locationStore, checkoutStore } = useStores();
  const { currencyCode } = locationStore;
  const [hasError, setHasError] = useState(false);

  // Manage group state.
  const [group, groupItems] = useMemo(() => {
    const _group = menuDataStore.modifierGroupsById[modGroup.modifierGroupId];
    const _availableMods = Object.entries(_group?.modifiers ?? {})
      .filter(([id, mod]) => modGroup.modifierIds.includes(id) && mod.isFulfillable)
      .map(([_, mod]) => mod);

    return [
      _availableMods.length > 0 ? _group : null,
      _availableMods.sort((a, b) => a.display_position - b.display_position),
    ];
  }, [menuDataStore.modifierGroupsById, modGroup, menuDataStore.menuItemHash]);

  // Manage checkbox/radio select state.
  const [isCheckbox, Control, selectedValues] = useMemo(() => {
    const _isCheckbox = Boolean(group && (group?.max_selected > 1 || group?.min_selected === 0));
    return [
      _isCheckbox,
      _isCheckbox ? CustomCheckbox : Radio,
      _isCheckbox
        ? displayedItem.selectedMods
        : displayedItem.mods[group?.id ?? '']?.find((mod) => mod.selected)?.menu_item_id,
    ];
  }, [displayedItem.mods, displayedItem.selectedMods, group]);

  useEffect(() => {
    const _hasError = Boolean(group?.is_required && !displayedItem.isValid(group));
    if (_hasError && groupRef.current) {
      addHTMLElementError(groupRef.current);
    }
    setHasError(_hasError);
  }, [modGroup, displayedItem, displayedItem.mods, group, addHTMLElementError, removeHTMLElementError]);

  const handleControlChange = (event: {
    target: { checked?: boolean; value?: string; type?: string; disabled?: boolean };
  }) => {
    if (!group) {
      return;
    }

    const menuItemId = event.target.value ?? '';
    const { trackClickSelectItemModifier, trackClickDeselectItemModifier } = itemModalTrackingEvents;

    displayedItem.toggleModifier(group, menuItemId);

    const trackingFields = {
      type: event.target?.type,
      value: event.target?.value,
      disabled: event.target?.disabled,
      checked: event.target?.checked,
      modifierGroup: group?.heading_name,
      cartItem: cartFormatting.formatCartItemForTracking(cartItem),
    };

    // Tracking
    if (event.target?.checked) {
      trackClickSelectItemModifier(trackingFields);
    } else {
      trackClickDeselectItemModifier(trackingFields);
    }

    // A modifierGroup will be valid when the minimum selections for that group are met,
    // but the cartModifier will only be valid when ALL of its modifierGroups are valid.
    if (displayedItem.isValid(group) && groupRef.current) {
      removeHTMLElementError(groupRef.current);
    }

    if (displayedItem.isValid()) {
      removeModWithMissingSelections(displayedItem);
    }
  };

  if (!group || group.integrations?.hide_from_guests) {
    return null;
  }
  const requiredModifier = group.necessity === 'Required';
  const GroupRequirement = requiredModifier ? RequiredGroup : OptionalGroup;

  return (
    <Container
      data-testid={`group_${modGroup.modifierGroupId}`}
      key={`group_${modGroup.modifierGroupId}`}
      ref={groupRef}
    >
      <Header>
        <ModifierGroupHeading>
          <Heading>{group.heading_name}</Heading>
          <GroupRequirement className={hasError && submitPressed ? 'error--selection-required' : ''}>
            {requiredModifier && <WarningIcon />}
            <Text styles={Text.Styles.Medium12} color={requiredModifier ? '#643B00' : Text.Colors.SystemBlack}>
              {group.necessity}
            </Text>
          </GroupRequirement>
        </ModifierGroupHeading>
        <Description>
          <span className={hasError && submitPressed ? 'error' : ''}>{group.description}</span>
        </Description>
      </Header>
      <Body>
        <Form.Item>
          <Control.Group
            style={{ width: '100%' }}
            disabled={!checkoutStore.isAllowedToEditItem(displayedItem)}
            // @ts-expect-error - we know that we only accept checkbox event change handlers here, but TS doesn't know that
            onChange={isCheckbox ? undefined : handleControlChange}
            value={selectedValues}
          >
            {groupItems.map((item, index) => (
              <ModifierView
                key={item.id}
                Control={Control}
                menuItem={item}
                currencyCode={currencyCode}
                group={group}
                last={index === groupItems.length - 1}
                onChange={isCheckbox ? handleControlChange : undefined}
              />
            ))}
          </Control.Group>
        </Form.Item>
      </Body>
    </Container>
  );
}

export default observer(ModifierGroupView);
